import { useTranslation } from 'react-i18next';
import { Paperclip, X } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Item, ItemActions, ItemContent, ItemDescription, ItemMedia, ItemTitle } from '@/components/ui/item';
import type { AttachmentType } from '@/entities/consignments';
import type { ReceivePendingAttachment } from '@/entities/receive';
import { formatFileSize, useImagePreviewUrl } from '@/shared/lib';
import { FilterSelect, type SelectOption } from '@/shared/ui';

interface ConfirmArrivalPendingDocumentProps {
  attachment: ReceivePendingAttachment;
  typeOptions: SelectOption<AttachmentType>[];
  onTypeChange: (tempId: string, attachmentType: AttachmentType) => void;
  onRemove: (tempId: string) => void;
}

/**
 * A file waiting for the receipt to be recorded.
 *
 * Unlike the stored ones it is not downloadable and it still carries its type
 * as an editable field: the backend takes one `attachmentType` per file, so the
 * choice has to be made before submit, not after.
 */
export function ConfirmArrivalPendingDocument({
  attachment,
  typeOptions,
  onTypeChange,
  onRemove
}: ConfirmArrivalPendingDocumentProps) {
  const { t } = useTranslation();
  const { tempId, file, attachmentType } = attachment;
  const previewUrl = useImagePreviewUrl(file);

  return (
    <Item>
      {previewUrl ? (
        <ItemMedia variant="image">
          <img src={previewUrl} alt={file.name} />
        </ItemMedia>
      ) : (
        <ItemMedia>
          <Paperclip />
        </ItemMedia>
      )}

      <ItemContent>
        <ItemTitle>{file.name}</ItemTitle>
        <ItemDescription>{formatFileSize(file.size)}</ItemDescription>
      </ItemContent>

      <ItemActions>
        <FilterSelect
          value={attachmentType}
          onChange={(value) => onTypeChange(tempId, value)}
          options={typeOptions}
          placeholder={t('onlinesrf.attachment.type.placeholder')}
          width="lg"
        />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="h-8 w-8 p-0"
          onClick={() => onRemove(tempId)}
          aria-label={`${t('newcmr.remove.attachment.label', 'Remove Attachment')} — ${file.name}`}>
          <X className="h-4 w-4" />
        </Button>
      </ItemActions>
    </Item>
  );
}
